import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { isTerminalRunStatus, type OperationRunDetail } from "../lib/operationRun.js";

// Feature 0010 (ML Node Run Lifecycle): editor-scoped run state for the ML
// node "Run & preview" buttons (model_evaluate / model_predict). The run state
// AND the in-flight fetch live here — above the node inspector — so a run
// survives the inspector unmounting (Config↔Advisor tab switch, deselecting /
// reselecting the node, switching nodes and back). The fetch carries an
// AbortController; aborting it (Stop, or the editor unmounting) closes the
// socket, and the hub interrupts the in-flight DuckDB query. Nothing outlives
// the editor.

export type MLRunPreview = { columns: Array<{ name: string; type?: string }>; rows: unknown[][]; rowCount: number };

export type MLNodeRunState = {
  running: boolean;
  /** Epoch ms when the current run began (for "Running since {time}"). */
  startedAt: number | null;
  preview: MLRunPreview | null;
  /** Evaluate only — latest evaluation metrics fetched after a successful run. */
  metrics: Record<string, unknown> | null;
  error: string | null;
};

const EMPTY_RUN: MLNodeRunState = {
  running: false, startedAt: null, preview: null, metrics: null, error: null,
};

type StartRunOpts = {
  operationId: string;
  flushAndSave: () => Promise<boolean>;
  /** When set (evaluate), fetch this model's latest evaluation metrics after a successful run. */
  evaluateModelId?: string | null;
};

type MLNodeRunContextValue = {
  getRun: (nodeId: string) => MLNodeRunState;
  startRun: (nodeId: string, opts: StartRunOpts) => void;
  stopRun: (nodeId: string) => void;
};

const MLNodeRunContext = createContext<MLNodeRunContextValue | null>(null);

export function MLNodeRunProvider({
  children,
  onRunDetail,
}: {
  children: ReactNode;
  /**
   * ADR-0128 follow-up — invoked on every poll tick (incl. the terminal one)
   * with the run's full detail, so the editor can reflect the ML node's run
   * onto the canvas badge through the SAME `ingestRunDetail` path chain runs
   * use. Without this the predict/evaluate node never pulses on the canvas
   * (this context drives only the inspector preview). Optional so the provider
   * stays usable without an editor canvas.
   */
  onRunDetail?: (detail: OperationRunDetail) => void;
}) {
  const [runs, setRuns] = useState<Record<string, MLNodeRunState>>({});
  const controllers = useRef<Map<string, AbortController>>(new Map());
  // ADR-0128 — active run per node so Stop can hit the cancel route.
  const runRefByNode = useRef<Map<string, { operationId: string; runId: string }>>(new Map());
  // Latest onRunDetail in a ref so the long-lived poll closure always calls the
  // current callback (never a stale one) without re-creating `startRun`.
  const onRunDetailRef = useRef(onRunDetail);
  onRunDetailRef.current = onRunDetail;

  const patch = useCallback((nodeId: string, p: Partial<MLNodeRunState>) => {
    setRuns((prev) => ({ ...prev, [nodeId]: { ...(prev[nodeId] ?? EMPTY_RUN), ...p } }));
  }, []);

  const startRun = useCallback((nodeId: string, opts: StartRunOpts) => {
    // Guard against double-run (button is also disabled, but be defensive).
    if (controllers.current.has(nodeId)) return;
    const controller = new AbortController();
    controllers.current.set(nodeId, controller);
    patch(nodeId, { running: true, startedAt: Date.now(), error: null, preview: null, metrics: null });
    void (async () => {
      try {
        const ok = await opts.flushAndSave();
        if (!ok) {
          patch(nodeId, { error: "Couldn't save pending edits — fix the validation error and retry." });
          return;
        }
        // ADR-0128 — async run: POST → 202 { runId } | 409 { runId } (attach);
        // poll runs/:runId until terminal; then fetch the landed (capped)
        // preview from the node cache.
        let runId: string;
        try {
          const res = await apiFetch<{ runId: string }>(
            `/api/tenant/operations/${encodeURIComponent(opts.operationId)}/nodes/${encodeURIComponent(nodeId)}/execute-preview`,
            // ADR-0130: evaluate/predict Run gates ancestors (observe the freshness
            // gate) like transform Run + viz refresh, instead of force-refreshing
            // the whole chain (which re-ran a ~200s provider on every click). The
            // route always force-executes the target; only upstream is gated.
            { method: "POST", body: { forceRefresh: false }, signal: controller.signal },
          );
          runId = res.runId;
        } catch (e) {
          if (e instanceof ApiRequestError && e.status === 409 && e.code === "run_in_progress" && typeof e.data?.runId === "string") {
            runId = e.data.runId;
          } else {
            throw e;
          }
        }
        runRefByNode.current.set(nodeId, { operationId: opts.operationId, runId });

        // Poll until terminal.
        let runError: string | null = null;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (controller.signal.aborted) throw new DOMException("aborted", "AbortError");
          const detail = await apiFetch<OperationRunDetail>(
            `/api/tenant/operations/${encodeURIComponent(opts.operationId)}/runs/${encodeURIComponent(runId)}`,
            { signal: controller.signal },
          );
          // Reflect this tick onto the canvas (per-node badges + whole-run
          // target pulse) via the editor's ingestRunDetail. Terminal ticks flow
          // through too, so the badge lands on success/error rather than
          // sticking on "running".
          onRunDetailRef.current?.(detail);
          if (isTerminalRunStatus(detail.run.status)) {
            if (detail.run.status === "failed") runError = detail.run.error ?? "Run failed.";
            if (detail.run.status === "cancelled") throw new DOMException("aborted", "AbortError");
            break;
          }
          await new Promise((r) => setTimeout(r, 1000));
        }
        if (runError) { patch(nodeId, { error: runError }); return; }

        let preview: MLRunPreview | null = null;
        try {
          const cache = await apiFetch<{ transformResult: MLRunPreview | null }>(
            `/api/tenant/operations/${encodeURIComponent(opts.operationId)}/nodes/${encodeURIComponent(nodeId)}/cache?port_id=tabular_out`,
            { signal: controller.signal },
          );
          preview = cache.transformResult ?? null;
        } catch { /* preview best-effort — the run itself succeeded */ }
        patch(nodeId, { preview });
        // The run recorded a model_evaluations row — pull its metrics to show inline.
        if (opts.evaluateModelId) {
          try {
            const ev = await apiFetch<{ evaluations: Array<{ status: string; metrics: Record<string, unknown> | null }> }>(
              `/api/tenant/models/${encodeURIComponent(opts.evaluateModelId)}/evaluations`,
              { signal: controller.signal },
            );
            const latest = ev.evaluations?.find((e) => e.status === "success") ?? ev.evaluations?.[0] ?? null;
            patch(nodeId, { metrics: latest?.metrics ?? null });
          } catch { /* metrics best-effort */ }
        }
      } catch (e) {
        // AbortError = user hit Stop or left the editor — not a failure.
        if (e instanceof DOMException && e.name === "AbortError") {
          patch(nodeId, { preview: null, metrics: null, error: null });
        } else {
          patch(nodeId, { error: e instanceof Error ? e.message : String(e) });
        }
      } finally {
        controllers.current.delete(nodeId);
        runRefByNode.current.delete(nodeId);
        patch(nodeId, { running: false, startedAt: null });
      }
    })();
  }, [patch]);

  const stopRun = useCallback((nodeId: string) => {
    // Abort the local poll loop, then best-effort ask the hub to cancel the run
    // so it doesn't keep executing with no consumer.
    controllers.current.get(nodeId)?.abort();
    const ref = runRefByNode.current.get(nodeId);
    if (ref) {
      void apiFetch(
        `/api/tenant/operations/${encodeURIComponent(ref.operationId)}/runs/${encodeURIComponent(ref.runId)}/cancel`,
        { method: "POST" },
      ).catch(() => { /* run may already be terminal — abort covered the UI */ });
    }
  }, []);

  const getRun = useCallback((nodeId: string) => runs[nodeId] ?? EMPTY_RUN, [runs]);

  // Editor unmount → abort the local poll loops only. ADR-0128 deliberately
  // RETIRED client-disconnect cancellation: the run keeps executing server-side
  // and lands its cache (visible via the run primitive from any session).
  // Explicit Stop is the only gesture that cancels a run. (Also StrictMode-safe:
  // a synthetic unmount must never kill a real run.)
  useEffect(() => {
    const map = controllers.current;
    return () => {
      for (const c of map.values()) c.abort();
    };
  }, []);

  return (
    <MLNodeRunContext.Provider value={{ getRun, startRun, stopRun }}>
      {children}
    </MLNodeRunContext.Provider>
  );
}

export function useMLNodeRun(): MLNodeRunContextValue {
  const v = useContext(MLNodeRunContext);
  if (!v) throw new Error("useMLNodeRun must be used within MLNodeRunProvider");
  return v;
}
